/**
 * Calculates the index (place) where the visibility trigger should be placed in the list.
 * Useful when implementing on-scroll pagination.
 * @param entitiesCount - Number of entities in the list.
 * @param batchSize - Size of each batch in the list.
 * @returns The index (place) where the visibility trigger should be placed in the list.
 */
export const calculateVisibilityTriggerIndex = (
  entitiesCount: number,
  batchSize: number
): number => {
  const deductionAmount = batchSize > 3 ? Math.floor(batchSize / 3) : 1;
  // Additionally deducting 1 since indexes are starting from 0.
  const thresholdIndex = entitiesCount - deductionAmount - 1;

  return thresholdIndex >= 0 ? thresholdIndex : 0;
};

export const ELLIPSIS = "ellipsis" as const;

export type PaginationItem = number | typeof ELLIPSIS;

/**
 * Compute the page items to render: first/last page, a window of `siblings`
 * around the current page, and `ellipsis` gaps.
 *
 * e.g. page 6 of 10, siblings 1 -> [1, ellipsis, 5, 6, 7, ellipsis, 10]
 *
 * Lives here rather than with the search feature because it backs the one pagination
 * component the whole site renders.
 */
export const getPaginationRange = (
  page: number,
  pages: number,
  siblings = 1
): PaginationItem[] => {
  const range = (start: number, end: number) =>
    Array.from({ length: end - start + 1 }, (_, offset) => start + offset);

  // first + last + current + 2*siblings + 2 ellipses
  const maxVisible = siblings * 2 + 5;

  if (pages <= maxVisible) {
    return range(1, pages);
  }

  const left = Math.max(page - siblings, 1);
  const right = Math.min(page + siblings, pages);
  const showLeftEllipsis = left > 2;
  const showRightEllipsis = right < pages - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    return [...range(1, siblings * 2 + 3), ELLIPSIS, pages];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    return [1, ELLIPSIS, ...range(pages - (siblings * 2 + 2), pages)];
  }

  return [1, ELLIPSIS, ...range(left, right), ELLIPSIS, pages];
};
